/**
 * Valores NOMINALES (pesos reales, sin "caquificar") de un presupuesto y sus
 * movimientos. Fuente única de las fórmulas que antes vivían inline en los
 * tooltips del PresupuestoDrawer, reutilizadas por el export a PDF (modo nominal).
 *
 * "Nominal" = el peso del momento, sin reexpresar a pesos de hoy vía índice CAC.
 * Para presupuestos indexados, los adicionales se valúan con su PROPIO
 * cotizacion_snapshot (el valor del momento en que se agregaron, no el actual).
 */

import { snapshotCacIndice } from '../cac/pickCac';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Solo el adicional aprobado suma; uno sin estado (anterior a los estados) cuenta como aprobado. */
export const adicionalSuma = (a) => (a?.estado ?? 'aprobado') === 'aprobado';

/**
 * Presupuestado nominal en pesos (o en su moneda nativa si el presupuesto es plano).
 * - Indexado (CAC/USD): monto_ingresado original + Σ pesos de cada adicional (pesosDeAdicional).
 * - Plano: presupuesto.monto (ya incluye adicionales, en su moneda nativa).
 * cacModo: modo CAC de la empresa, para elegir la variante en snapshots nuevos
 * (snapshotCacIndice también resuelve el shape viejo cac_indice/cac_general).
 */
export function calcPresupuestadoNominal(presupuesto, cacModo = 'legacy') {
  const p = presupuesto || {};
  if (p.indexacion === 'CAC' || p.indexacion === 'USD') {
    const adicionales = (p.adicionales || []).filter(adicionalSuma)
      .reduce((s, a) => s + (pesosDeAdicional(a, p, cacModo) ?? 0), 0);
    return num(p.monto_ingresado) + adicionales;
  }
  return num(p.monto);
}

/**
 * Pesos de un adicional de un presupuesto indexado: los que tipeó el usuario (monto_ingresado);
 * los adicionales viejos no los guardan y se reconstruyen con la cotización de su propio snapshot.
 * Sin cotización, null.
 */
export function pesosDeAdicional(adicional, presupuesto, cacModo = 'legacy') {
  if (adicional?.monto_ingresado != null) return num(adicional.monto_ingresado);
  const snap = adicional?.cotizacion_snapshot || {};
  const cotiz = presupuesto?.indexacion === 'CAC'
    ? snapshotCacIndice(snap, presupuesto.cac_tipo, cacModo)
    : (snap.dolar_blue ?? null);
  return cotiz ? num(adicional?.monto) * num(cotiz) : null;
}

/**
 * Ejecutado nominal en pesos: suma de los movimientos al peso del momento.
 * Usa la equivalencia en ARS guardada en cada movimiento (que ya refleja el valor
 * nominal pagado), con fallback al total nativo. Mismo criterio por fila que el
 * modo nominal de buildControlPresupuestoData, para que totales y filas coincidan.
 */
export function calcEjecutadoNominalARS(movimientos, baseCalculo = 'total') {
  const campo = baseCalculo === 'subtotal' ? 'subtotal' : 'total';
  return (movimientos || []).reduce((s, m) => {
    const eq = m?.equivalencias?.[campo] || m?.equivalencias?.total || {};
    const ars = eq.ars != null ? num(eq.ars) : num(m?.[campo] ?? m?.total);
    return s + ars;
  }, 0);
}
